/**
 * CLI subcommand for querying and triggering test suite (workflow test execution) runs.
 *
 * @module TestSuiteCLI
 */

import process from 'node:process';
import { resolveConfig, getArgValue, hasFlag } from './auth.js';
import { apiRequest } from './cli-helpers.js';
import { runRemoteTest } from './remote-test.js';
import { isTriageEnabledForExecution } from './triage.js';

// ---------------------------------------------------------------------------
// Types (matching API serializers in apps/api/src/workflows/routes/shared.ts)
// ---------------------------------------------------------------------------

type Pagination = {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

type TestExecutionSummary = {
  id: string;
  status: string;
  totalWorkflows: number;
  completedWorkflows: number;
  failedWorkflows: number;
  successfulWorkflows: number;
  skippedWorkflows: number;
  /** @deprecated Quarantine removed; always 0. Use degradedWorkflows. */
  quarantinedWorkflows: number;
  degradedWorkflows?: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

type TestExecution = TestExecutionSummary & {
  orgId: string;
  mode: string;
  triggeredByUserId: string | null;
  triggeredReason: string | null;
  errorMessage: string | null;
  updatedAt: string;
};

type TestRunResult = {
  id: string;
  testRunId: string;
  workflowId: string;
  nodeId: string;
  status: string;
  message: string | null;
  issueId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
};

type TestRun = {
  id: string;
  executionId: string | null;
  workflowId: string;
  status: string;
  snapshotVersion: number;
  workflowRunId: string | null;
  triggeredByUserId: string | null;
  triggeredReason: string | null;
  linkedIssueId: string | null;
  isQuarantined: boolean;
  errorMessage: string | null;
  skipReason: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  workflowName: string | null;
  results: TestRunResult[];
};

type TestExecutionListResponse = {
  ok: boolean;
  error?: string;
  data: TestExecutionSummary[];
  pagination: Pagination;
};

type TestExecutionDetailResponse = {
  ok: boolean;
  error?: string;
  execution: TestExecution;
  testRuns: TestRun[];
};

type RunStep = {
  id: string;
  runId: string;
  nodeId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  orderIndex: number;
  metadataJson: Record<string, unknown> | null;
  createdAt: string;
};

type WorkflowRun = {
  id: string;
  workflowId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  steps: RunStep[];
};

type RunWithStepsResponse = {
  ok: boolean;
  error?: string;
  data: { run: WorkflowRun };
};

type DiagnosticJson = {
  category?: string;
  title?: string;
  analysis?: string;
  recommendation?: string;
  smokingGun?: { type: string; description: string; detail?: string } | null;
};

type DiagnosticThought = {
  content: string;
  toolName: string | null;
  orderIndex: number;
  createdAt: string;
};

type FailureDiagnosticResponse = {
  ok: boolean;
  error?: string;
  data: {
    id: string;
    status: string;
    diagnostic: DiagnosticJson | null;
    createdAt: string;
    updatedAt: string;
    thoughts?: DiagnosticThought[];
  } | null;
};

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

const STATUS_ICONS: Record<string, string> = {
  completed: '✅',
  completed_with_errors: '⚠️',
  success: '✅',
  failed: '❌',
  running: '🔵',
  queued: '⏳',
  canceled: '🚫',
  timeout: '⏰',
  skipped: '⏭️',
  flaky_success: '🟡',
  pending: '⏳',
};

function statusIcon(status: string): string {
  return STATUS_ICONS[status] ?? '❓';
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const diffMs = Date.now() - new Date(iso).getTime();
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function computeDuration(startedAt: string | null, finishedAt: string | null): string {
  if (!startedAt || !finishedAt) return '—';
  const ms = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (ms < 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainMins = minutes % 60;
  return remainMins > 0 ? `${hours}h ${remainMins}m` : `${hours}h`;
}

function pad(str: string, len: number): string {
  return str.padEnd(len);
}

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

async function handleList(argv: string[], apiUrl: string, token: string): Promise<void> {
  const jsonOutput = hasFlag(argv, '--json');
  const page = getArgValue(argv, '--page') ?? '1';
  const pageSize = getArgValue(argv, '--page-size') ?? '20';

  const params = new URLSearchParams();
  params.set('page', page);
  params.set('pageSize', pageSize);
  params.set('sortBy', 'createdAt');
  params.set('sortOrder', 'desc');

  const result = await apiRequest<TestExecutionListResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/test-executions?${params}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  const { data, pagination } = result;

  if (data.length === 0) {
    console.log('No test suites found.');
    return;
  }

  console.log(
    `Test Suites (page ${pagination.page}/${pagination.totalPages}, ${pagination.totalItems} total)\n`
  );

  for (const exec of data) {
    const icon = statusIcon(exec.status);
    const total = String(exec.totalWorkflows).padStart(3);
    const passed = String(exec.successfulWorkflows).padStart(3);
    const failed = String(exec.failedWorkflows).padStart(3);
    const time = formatRelative(exec.createdAt);

    console.log(
      `  ${icon} ${exec.id}  ${total} total  ${passed} passed  ${failed} failed   ${time}`
    );
  }

  console.log('');
}

async function handleGet(argv: string[], apiUrl: string, token: string): Promise<void> {
  const id = argv.find((a) => !a.startsWith('-'));
  if (!id) {
    console.error('Error: Missing execution ID. Usage: canary test-suite get <id>');
    process.exit(1);
  }

  const jsonOutput = hasFlag(argv, '--json');
  const failuresOnly = hasFlag(argv, '--failures-only');

  const result = await apiRequest<TestExecutionDetailResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/test-executions/${id}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    const output = failuresOnly
      ? { ...result, testRuns: result.testRuns.filter((r) => r.status === 'failed') }
      : result;
    console.log(JSON.stringify(output, null, 2));
    return;
  }

  const { execution, testRuns } = result;
  const duration = computeDuration(execution.startedAt, execution.finishedAt);

  console.log(`Test Suite: ${execution.id}`);
  console.log(`Status:     ${statusIcon(execution.status)} ${execution.status}`);
  console.log(`Duration:   ${duration}`);
  console.log(
    `Total: ${execution.totalWorkflows} | Passed: ${execution.successfulWorkflows} | Failed: ${execution.failedWorkflows} | Skipped: ${execution.skippedWorkflows} | Degraded: ${execution.degradedWorkflows ?? 0}`
  );
  console.log('');

  let runs = testRuns;
  if (failuresOnly) {
    runs = runs.filter((r) => r.status === 'failed');
    if (runs.length === 0) {
      console.log('No failing runs.');
      return;
    }
  }

  console.log('Runs:');
  for (const run of runs) {
    const icon = statusIcon(run.status);
    const name = run.workflowName ?? 'Unnamed';
    const dur = computeDuration(run.startedAt, run.finishedAt);
    const runId = run.workflowRunId ?? '—';

    console.log(`  ${icon}  ${pad(name, 30)} ${dur}`);
    console.log(`      wf: ${run.workflowId}  run: ${runId}`);

    if (run.errorMessage) {
      console.log(`      Error: ${run.errorMessage}`);
    }
    if (run.skipReason) {
      console.log(`      Skip reason: ${run.skipReason}`);
    }
    if (run.linkedIssueId) {
      console.log(`      Linked issue: ${run.linkedIssueId}`);
    }
  }

  console.log('');

  if (execution.failedWorkflows > 0) {
    const triageEnabled = await isTriageEnabledForExecution(apiUrl, token, execution.id);
    if (triageEnabled) {
      console.log(`Triage: canary triage get ${execution.id}`);
      console.log('');
    }
  }
}

async function handleDiagnostics(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  if (positional.length < 2) {
    console.error(
      'Error: Missing arguments. Usage: canary test-suite diagnostics <workflowId> <runId>'
    );
    process.exit(1);
  }

  const [workflowId, runId] = positional;
  const jsonOutput = hasFlag(argv, '--json');
  const showThoughts = hasFlag(argv, '--thoughts');

  const result = await apiRequest<FailureDiagnosticResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/runs/${runId}/failure-diagnostic${
      showThoughts ? '?includeThoughts=true' : ''
    }`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  if (!result.data) {
    console.log('No diagnostic available for this run.');
    return;
  }

  const { status, diagnostic } = result.data;

  if (status === 'pending' || status === 'running') {
    console.log(`Diagnostic is still in progress (status: ${status}).`);
    return;
  }

  if (status === 'failed') {
    console.log('Diagnostic analysis failed.');
    return;
  }

  if (!diagnostic) {
    console.log('Diagnostic completed but no analysis data available.');
    return;
  }

  console.log('Failure Diagnostic');
  console.log(`Status:     ${status}`);
  if (diagnostic.category) {
    console.log(`Category:   ${diagnostic.category}`);
  }
  if (diagnostic.title) {
    console.log(`Title:      ${diagnostic.title}`);
  }

  if (diagnostic.analysis) {
    console.log('');
    console.log('Analysis:');
    console.log(`  ${diagnostic.analysis}`);
  }

  if (diagnostic.recommendation) {
    console.log('');
    console.log('Recommendation:');
    console.log(`  ${diagnostic.recommendation}`);
  }

  if (diagnostic.smokingGun) {
    console.log('');
    console.log('Smoking Gun:');
    console.log(`  > ${diagnostic.smokingGun.description}`);
    if (diagnostic.smokingGun.detail) {
      console.log(`    ${diagnostic.smokingGun.detail}`);
    }
  }

  if (showThoughts) {
    const thoughts = result.data.thoughts ?? [];
    console.log('');
    console.log(`Agent reasoning (${thoughts.length} step${thoughts.length === 1 ? '' : 's'}):`);
    if (thoughts.length === 0) {
      console.log('  (no reasoning trace captured for this diagnostic)');
    } else {
      for (const t of thoughts) {
        const tool = t.toolName ? ` → ${t.toolName}()` : '';
        console.log(`  [${t.orderIndex}]${tool}`);
        for (const line of t.content.split('\n')) {
          console.log(`      ${line}`);
        }
      }
    }
  }

  console.log('');
}

async function handleSteps(argv: string[], apiUrl: string, token: string): Promise<void> {
  const positional = argv.filter((a) => !a.startsWith('-'));
  if (positional.length < 2) {
    console.error('Error: Missing arguments. Usage: canary test-suite steps <workflowId> <runId>');
    process.exit(1);
  }

  const [workflowId, runId] = positional;
  const jsonOutput = hasFlag(argv, '--json');

  const result = await apiRequest<RunWithStepsResponse>(
    apiUrl,
    token,
    'GET',
    `/workflows/${workflowId}/runs/${runId}`
  );

  if (!result.ok) {
    console.error(`Error: ${result.error ?? 'Unknown error'}`);
    process.exit(1);
  }

  if (jsonOutput) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }

  const { run } = result.data;
  const duration = computeDuration(run.startedAt, run.finishedAt);

  console.log(`Run: ${run.id} (${run.status})`);
  console.log(`Duration: ${duration}`);

  if (run.errorMessage) {
    console.log(`Error: ${run.errorMessage}`);
  }

  const steps = (run.steps ?? []).sort((a, b) => a.orderIndex - b.orderIndex);

  if (steps.length === 0) {
    console.log('\nNo steps recorded.');
    return;
  }

  console.log(`\nSteps (${steps.length}):`);
  for (const step of steps) {
    const idx = String(step.orderIndex + 1).padStart(2);
    const icon = statusIcon(step.status);
    const nodeId = step.nodeId.slice(0, 8);
    const dur = computeDuration(step.startedAt, step.finishedAt);

    console.log(`  ${idx}. ${icon}  ${pad(nodeId, 10)} ${dur}`);

    if (step.errorMessage) {
      console.log(`       Error: ${step.errorMessage}`);
    }
  }

  console.log('');
}

// ---------------------------------------------------------------------------
// Help
// ---------------------------------------------------------------------------

function printTestSuiteHelp(): void {
  console.log(
    [
      'Usage: canary test-suite <sub-command> [options]',
      '',
      'Sub-commands:',
      '  run [options]                            Trigger a test suite and stream results',
      '  list [options]                           List recent test suites',
      '  get <id> [options]                       Get test suite detail with runs',
      '  diagnostics <workflowId> <runId>         Get failure diagnostic for a run (--thoughts for the agent reasoning trace)',
      '  steps <workflowId> <runId>               Get workflow run steps',
      '',
      'After a run, the suite id can be passed to triage commands:',
      '  canary triage wait <suiteId>             Block until AI clusters failures (beta)',
      '  canary triage get <suiteId>              Show clustered failures + suggested actions',
      '  canary triage get latest                 Resolves to the most recent suite',
      '',
      'Run options:',
      '  --suite <name|id>     Run a named, layered test suite (members run in layer order)',
      '  --application <name|id>  Target a specific application (alias: --property)',
      '  --environment <name|id>  Target a specific environment (required with --env-mode force)',
      '  --env-mode <mode>     mixed (default — each flow on its preferred env) | force (pin to --environment)',
      '  --tag <tag>           Filter workflows by tag',
      '  --name-pattern <pat>  Filter workflows by name pattern',
      '  --verbose, -v         Show all SSE events',
      '',
      'List options:',
      '  --page <n>            Page number (default: 1)',
      '  --page-size <n>       Page size (default: 20)',
      '',
      'Get options:',
      '  --failures-only       Show only failing test runs',
      '',
      'Output options:',
      '  --json                Output raw JSON',
      '',
      'Common options:',
      '  --env <env>           Target environment (prod, dev)',
      '  --api-url <url>       API URL override',
      '  --token <key>         API token override',
      '',
      'Examples:',
      '  canary test-suite run                           Run all workflow tests',
      '  canary test-suite run --suite "Checkout flow"   Run a named, layered suite',
      '  canary test-suite run --tag smoke               Run smoke-tagged workflows',
      '  canary test-suite run --suite "Checkout flow" --env-mode force --environment staging',
      '  canary test-suite run --suite "Browser tests" --external-spec tests/smoke.spec.js',
      '  canary test-suite run --suite "Browser tests" --external-grep "checkout"',
      '  canary test-suite run --application "My App" --environment staging',
      '  canary test-suite list                          List recent suite executions',
      '  canary test-suite get <id> --failures-only      Show only failures',
      '  canary test-suite get <id> --failures-only --json  Failures as JSON',
    ].join('\n')
  );
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export async function runTestSuite(argv: string[]): Promise<void> {
  const [subCommand, ...rest] = argv;

  if (!subCommand || subCommand === 'help' || hasFlag(argv, '--help', '-h')) {
    printTestSuiteHelp();
    return;
  }

  if (subCommand === 'run') {
    // Delegate to runRemoteTest — pass through remaining args
    await runRemoteTest(rest);
    return;
  }

  const { apiUrl, token } = await resolveConfig(argv);

  switch (subCommand) {
    case 'list':
      await handleList(rest, apiUrl, token);
      break;
    case 'get':
      await handleGet(rest, apiUrl, token);
      break;
    case 'diagnostics':
      await handleDiagnostics(rest, apiUrl, token);
      break;
    case 'steps':
      await handleSteps(rest, apiUrl, token);
      break;
    default:
      console.error(`Unknown sub-command: ${subCommand}`);
      printTestSuiteHelp();
      process.exit(1);
  }
}
